import React, { useState } from 'react';
import { ICONS } from '../constants';
import SupportEmailLink from './SupportEmailLink';

interface LandingPageProps {
  onLoginClick: () => void;
  isEmailLinkUrl?: boolean;
  emailLinkError?: string | null;
  onClearEmailLinkError?: () => void;
  onCompleteSignInWithEmailLink?: (email?: string) => Promise<void>;
  /** Topic from landing handoff – show "what you entered" and what happens next */
  handoffTopic?: string | null;
  handoffFromLanding?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onLoginClick,
  isEmailLinkUrl,
  emailLinkError,
  onClearEmailLinkError,
  onCompleteSignInWithEmailLink,
  handoffTopic,
  handoffFromLanding
}) => {
  const [completeSignInEmail, setCompleteSignInEmail] = useState('');
  const [completeSignInLoading, setCompleteSignInLoading] = useState(false);

  const handleCompleteSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onCompleteSignInWithEmailLink || !completeSignInEmail.trim()) return;
    setCompleteSignInLoading(true);
    onClearEmailLinkError?.();
    try {
      await onCompleteSignInWithEmailLink(completeSignInEmail.trim());
    } finally {
      setCompleteSignInLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-click-dark flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-lab-pattern"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-click-red/10 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-click-cyan/5 blur-[100px] rounded-full"></div>
      
      {/* Content */}
      <div className="relative z-10 text-center max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <ICONS.FlaskConical className="w-16 h-16 text-click-red animate-pulse" />
          <div>
            <h1 className="text-5xl font-display font-black tracking-widest text-white">
              CLICK<span className="text-click-red">TUBE</span>
            </h1>
            <span className="text-xs text-gray-500 tracking-[0.3em]">CTR SCIENCE LAB</span>
          </div>
        </div>

        {/* Complete sign-in (email link) - when user opened link without stored email */}
        {isEmailLinkUrl && onCompleteSignInWithEmailLink && (
          <div className="mb-8 p-6 bg-click-card border border-click-border rounded-2xl text-right">
            <p className="text-white font-bold mb-2">השלם התחברות עם האימייל שקיבלת את הקישור</p>
            <p className="text-sm text-gray-400 mb-4">הזן את כתובת האימייל שאליה נשלח לינק ההתחברות</p>
            {emailLinkError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm flex items-center justify-between gap-2">
                <span>{emailLinkError}</span>
                {onClearEmailLinkError && (
                  <button type="button" onClick={onClearEmailLinkError} className="shrink-0 text-red-300 hover:text-white" aria-label="סגור">
                    <ICONS.X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            <form onSubmit={handleCompleteSignIn} className="space-y-3">
              <input
                type="email"
                value={completeSignInEmail}
                onChange={(e) => setCompleteSignInEmail(e.target.value)}
                placeholder="אימייל"
                required
                className="w-full bg-click-dark border border-click-border rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-click-red"
                dir="ltr"
              />
              <button
                type="submit"
                disabled={completeSignInLoading}
                className="w-full bg-click-red text-white font-bold py-3 rounded-xl hover:bg-white hover:text-click-red transition-all disabled:opacity-50"
              >
                {completeSignInLoading ? 'מתחבר...' : 'השלם התחברות'}
              </button>
            </form>
          </div>
        )}

        {/* Handoff context: what they entered and what happens next */}
        {(handoffTopic || handoffFromLanding) && (
          <div className="mb-6 p-4 bg-click-card/80 border border-click-border rounded-xl text-right">
            {handoffTopic && (
              <>
                <p className="text-sm text-gray-400 mb-1">מה שהזנת:</p>
                <p className="text-white font-bold mb-3 line-clamp-2 break-words">
                  {handoffTopic.length > 80 ? `${handoffTopic.slice(0, 80)}...` : handoffTopic}
                </p>
              </>
            )}
            <p className="text-sm text-gray-300">
              אחרי ההתחברות נצור עבורך פרויקט עם הנושא והתמונה שהעלית – תגיע ישר לרעיונותאמבניילים.
            </p>
          </div>
        )}

        {/* Tagline */}
        <p className="text-xl text-gray-300 mb-12 leading-relaxed">
          המעבדה ליצירת טאמבניילים שמושכים קליקים
        </p>

        {/* Login Button */}
        <button
          onClick={onLoginClick}
          className="group relative bg-click-red text-white font-bold text-lg py-4 px-12 rounded-xl hover:bg-white hover:text-click-red transition-all duration-300 shadow-[0_0_30px_rgba(255,0,60,0.3)] hover:shadow-[0_0_40px_rgba(255,0,60,0.5)]"
        >
          <span className="flex items-center gap-3">
            <ICONS.LogIn className="w-5 h-5" />
            התחבר או הירשם
          </span>
        </button>

        {/* Subtext */}
        <p className="text-sm text-gray-500 mt-6">
          הרשמה חינם • 50 קרדיטים במתנה
        </p>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center">
        <p className="text-xs text-gray-600">
          © 2026 ClickTube Labs • The Science of Clicks
        </p>
        <SupportEmailLink className="text-xs text-gray-500 hover:text-gray-300 transition-colors" showEmail />
      </div>
    </div>
  );
};

export default LandingPage;
